import React, { useEffect, useRef } from "react";

import MarkerClusterer from "@google/markerclustererplus";
import { OverlappingMarkerSpiderfier } from "@map-my-customers/ts-overlapping-marker-spiderfier";

import MarkerIcon from "@mapmycustomers/shared/enum/MarkerIcon";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { EntityType, MapEntity } from "@mapmycustomers/shared/types/entity";
import GeoPoint from "@mapmycustomers/shared/types/shapes/GeoPoint";
import { convertGeoPointToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";

type MarkerOptions = google.maps.MarkerOptions;

interface Props {
  entities: MapEntity[];
  getGeoPoint: (entity: MapEntity, entityType: EntityType) => GeoPoint | undefined;
  getMarkerProps: (
    entity: MapEntity,
    entityType: EntityType,
    markerSize: MarkerSize,
    markerIcon: MarkerIcon
  ) => MarkerOptions;
  map: google.maps.Map;
  markerIcon: MarkerIcon;
  markerSize: MarkerSize;
  onClick?: (entity: MapEntity, marker: google.maps.Marker) => void;
}

const MultiMarkers: React.FC<Props> = ({
  entities,
  getGeoPoint,
  getMarkerProps,
  map,
  markerIcon,
  markerSize,
  onClick,
}) => {
  const markerSpiderfier = useRef<OverlappingMarkerSpiderfier>(
    new OverlappingMarkerSpiderfier(map)
  );
  const markerCluster = useRef<MarkerClusterer>(new MarkerClusterer(map));

  useEffect(() => {
    markerSpiderfier.current = new OverlappingMarkerSpiderfier(map, {
      keepSpiderfied: true,
      markersWontHide: true, // memory optimizations: it won't add event listeners to hide events
      markersWontMove: true, // memory optimizations: it won't add event listeners to move events
      nearbyDistance: 1, // only spiderify pin in exact same location
    });

    const bounds = new google.maps.LatLngBounds();
    const markers = entities
      .filter((entity) => getGeoPoint(entity, entity.entity))
      .map((entity) => {
        const latlng = convertGeoPointToLatLngLiteral(getGeoPoint(entity, entity.entity)!);
        bounds.extend(latlng);
        const marker = new google.maps.Marker({
          map,
          position: latlng,
          ...getMarkerProps(entity, entity.entity, markerSize, markerIcon),
        });
        marker.set("entityId", entity.id);
        markerSpiderfier.current.trackMarker(marker, () => {
          onClick?.(entity, marker);
        });
        return marker;
      });

    markerCluster.current = new MarkerClusterer(map, markers, {
      imagePath:
        "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
      maxZoom: 10,
    });

    if (entities.length > 0) {
      map.fitBounds(bounds);
      // zoom in if there's only one entity
      if (entities.length === 1) {
        map.setZoom(12);
      }
    } else {
      // use small zoom level when there's no pins
      map.setZoom(2);
    }

    return () => {
      markerSpiderfier.current.forgetAllMarkers();
      markerCluster.current.clearMarkers();
    };
  }, [entities, getGeoPoint, getMarkerProps, map, markerIcon, markerSize, onClick]);

  // nothing to render
  return null;
};

export default MultiMarkers;
